import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Flex, Icon, Typo, theme } from '../../../ui';
import { getUpcomingEvents } from '../data/mockCalendarEvents';
import { CALENDAR_EVENT_COLORS } from '../data/classroomConstants';

const WidgetContainer = styled.div`
  background: ${theme.colors.NEU_0};
  border-radius: 12px;
  border: 1px solid ${theme.colors.NEU_100};
  padding: 20px;
  width: 100%;
`;

const EventCard = styled(Flex)`
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;
  border: 3px solid ${({ $color }) => $color || theme.colors.PRIMARY};

  &:hover {
    background: ${theme.colors.NEU_50};
  }
`;

const TimeLabel = styled(Typo.SmallText)`
  font-weight: 600;
  min-width: 56px;
`;

const JoinButton = styled.button`
  padding: 6px 14px;
  background: ${theme.colors.PRIMARY};
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  &:hover {
    opacity: 0.9;
  }
`;

const EmptyState = styled(Flex)`
  padding: 16px 0;
`;

const formatEventTime = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const formatEventDay = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return 'Today';
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
  return date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
};

const TodayWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const upcomingEvents = getUpcomingEvents(5);

  if (upcomingEvents.length === 0) {
    return (
      <WidgetContainer>
        <Flex align='center' gap='8'>
          <Icon.Calendar color={theme.colors.PRIMARY} size='small' />
          <Typo.ContentSmallHeader>{t('classroom.upcomingSessions', 'Upcoming Sessions')}</Typo.ContentSmallHeader>
        </Flex>
        <EmptyState direction='column' align='center' gap='8'>
          <Typo.MediumText color={theme.colors.NEU_300}>{t('classroom.noUpcoming', 'No upcoming sessions')}</Typo.MediumText>
        </EmptyState>
      </WidgetContainer>
    );
  }

  return (
    <WidgetContainer>
      <Flex align='center' justify='space-between' style={{ marginBottom: 16 }}>
        <Flex align='center' gap='8'>
          <Icon.Calendar color={theme.colors.PRIMARY} size='small' />
          <Typo.ContentSmallHeader>{t('classroom.upcomingSessions', 'Upcoming Sessions')}</Typo.ContentSmallHeader>
        </Flex>
        <Typo.SmallText color={theme.colors.PRIMARY} style={{ cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate('/classroom')}>
          {t('classroom.viewAll', 'View all')}
        </Typo.SmallText>
      </Flex>

      <Flex gap='8' $wrap='wrap' direction='row'>
        {upcomingEvents.map((event) => (
          <EventCard
            key={event.id}
            align='center'
            justify='space-between'
            gap='12'
            $color={CALENDAR_EVENT_COLORS[event.type] || CALENDAR_EVENT_COLORS.live_session}
            onClick={() => navigate('/classroom')}
          >
            <Flex align='center' gap='12' style={{ flex: 1, minWidth: 0 }}>
              <Flex direction='column' gap='2'>
                <TimeLabel color={theme.colors.NEU_PRIMARY}>{event.allDay ? 'All day' : formatEventTime(event.start)}</TimeLabel>
                <Typo.SmallText color={theme.colors.NEU_300}>{formatEventDay(event.start)}</Typo.SmallText>
              </Flex>
              <Flex direction='column' gap='2' style={{ minWidth: 0 }}>
                <Typo.MediumText
                  style={{
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {event.extendedProps?.sectionName || event.title}
                </Typo.MediumText>
                <Typo.SmallText color={theme.colors.NEU_300}>{event.extendedProps?.teacherName}</Typo.SmallText>
              </Flex>
            </Flex>
            {event.type === 'live_session' && (
              <JoinButton
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {t('classroom.join', 'Join')}
              </JoinButton>
            )}
          </EventCard>
        ))}
      </Flex>
    </WidgetContainer>
  );
};

export default TodayWidget;
